import React from "react"
import {
  Box,
  HStack,
  SimpleGrid,
  Stack,
  Switch,
} from "@chakra-ui/react"
import { Control, useController, useWatch } from "react-hook-form"
import Select from "components/ReactHookForm/Select"
import TextField from "components/ReactHookForm/TextField"
import {
  WalletPassBuilderFormValues,
  PASS_AUXILIARY_FIELD_VALUE_OPTIONS,
} from "./walletPassBuilderTypes"
import { PassBuilderFieldRowLabel, PassBuilderFieldTitle } from "./PassBuilderTypography"
import PassBuilderFieldInput from "./PassBuilderFieldInput"
import {
  FIELD_CONTROL_STACK_SPACING,
  PASS_BUILDER_AUXILIARY_COL_GAP,
  PASS_BUILDER_FIELD_VARIANT,
} from "./passBuilderTokens"

type Props = {
  control: Control<WalletPassBuilderFormValues>
}

const passBuilderInputProps = {
  variant: PASS_BUILDER_FIELD_VARIANT,
  fullWidth: true,
  mb: 0,
} as const

// Two columns share the row rather than sitting at a fixed width, so each is
// about as wide as the single fields above.
const auxiliaryGridProps = {
  templateColumns: "repeat(2, minmax(0, 1fr))",
  gap: PASS_BUILDER_AUXILIARY_COL_GAP,
  flex: 1,
  minW: 0,
} as const

const AuxiliaryToggle = ({ control, name }: {
  control: Control<WalletPassBuilderFormValues>
  name: "auxiliary1Enabled" | "auxiliary2Enabled"
}) => {
  const { field } = useController({ control: control as any, name })

  return (
    <Switch
      size="md"
      isChecked={Boolean(field.value)}
      onChange={(e) => field.onChange(e.target.checked)}
      colorScheme="blue"
      flexShrink={0}
    />
  )
}

const PassBuilderAuxiliaryField = ({ control }: Props) => {
  const auxSources = useWatch({
    control,
    name: ["auxiliary1ValueSource", "auxiliary2ValueSource"],
  })
  const enabled = useWatch({
    control,
    name: ["auxiliary1Enabled", "auxiliary2Enabled"],
  })
  // Shown whenever the source is Static Text, disabled or not — a switched-off
  // field keeps its layout rather than collapsing a row out from under the one
  // beside it.
  const showStatic = auxSources.map((source) => source === "static")
  const anyStatic = showStatic.some(Boolean)

  return (
    <Stack spacing={FIELD_CONTROL_STACK_SPACING}>
      <HStack spacing={0} align="flex-end">
        <Box w="53px" flexShrink={0} />
        <SimpleGrid {...auxiliaryGridProps}>
          {(["auxiliary1Enabled", "auxiliary2Enabled"] as const).map((name, index) => (
            <HStack key={name} justify="space-between" align="center" mb={1}>
              <PassBuilderFieldTitle>{`Field ${index + 1}`}</PassBuilderFieldTitle>
              <AuxiliaryToggle control={control} name={name} />
            </HStack>
          ))}
        </SimpleGrid>
      </HStack>

      <HStack spacing={0} align="center">
        <PassBuilderFieldRowLabel>Label:</PassBuilderFieldRowLabel>
        <SimpleGrid {...auxiliaryGridProps}>
          {(["auxiliary1Label", "auxiliary2Label"] as const).map((name, index) => (
            <PassBuilderFieldInput key={name} width="100%">
              <TextField
                name={name}
                control={control as any}
                disabled={!enabled[index]}
                {...passBuilderInputProps}
              />
            </PassBuilderFieldInput>
          ))}
        </SimpleGrid>
      </HStack>

      <HStack spacing={0} align="center">
        <PassBuilderFieldRowLabel>Value:</PassBuilderFieldRowLabel>
        <SimpleGrid {...auxiliaryGridProps}>
          {(["auxiliary1ValueSource", "auxiliary2ValueSource"] as const).map((name, index) => (
            <PassBuilderFieldInput key={name} width="100%">
              <Select
                name={name}
                control={control as any}
                options={PASS_AUXILIARY_FIELD_VALUE_OPTIONS}
                disabled={!enabled[index]}
                {...passBuilderInputProps}
              />
            </PassBuilderFieldInput>
          ))}
        </SimpleGrid>
      </HStack>

      {anyStatic && (
        <HStack spacing={0} align="center">
          <Box w="53px" flexShrink={0} />
          <SimpleGrid {...auxiliaryGridProps}>
            {showStatic.map((isStatic, index) => (isStatic ? (
              <PassBuilderFieldInput key={`auxiliary${index + 1}StaticValue`} width="100%">
                <TextField
                  name={`auxiliary${index + 1}StaticValue` as any}
                  control={control as any}
                  disabled={!enabled[index]}
                  {...passBuilderInputProps}
                />
              </PassBuilderFieldInput>
            ) : <Box key={`auxiliary${index + 1}StaticValue`} />))}
          </SimpleGrid>
        </HStack>
      )}
    </Stack>
  )
}

export default PassBuilderAuxiliaryField
